import { memo } from 'react';

import { cn } from '@air/utils-tailwind';

// Mirrors the wire union (`ShortUrl` `presentationSettings.coverWidth`) so persisting surfaces need
// no boundary mapping between the preview value and the API value.
export type DesignPreviewCoverWidth = 'inset' | 'fullWidth' | 'immersive';

export type DesignPreviewCoverProps = {
  /** Hero/banner image source. */
  coverImageSrc: string;
  /**
   * How the cover band insets/spans: `inset` narrows it to the content-column width with rounded top
   * corners (page bg visible in the side gutters), `fullWidth` spans flush (the original look),
   * `immersive` spans flush + taller — the composition overlays the header bar on top of it.
   */
  coverWidth: DesignPreviewCoverWidth;
  /** When true, a bottom fade gradient blends the cover band into the page background. */
  coverFade: boolean;
  /** Resolved hex for the page background — the color the fade gradient blends into. */
  backgroundColor: string;
};

/**
 * The cover band of the mini preview: below the header bar for `inset`/`fullWidth`, or at the very top of
 * the card (with the header bar overlaid by the composition) for `immersive`. Owns the cover
 * inset/width and the optional fade-into-page gradient.
 */
export const DesignPreviewCover = memo(
  ({ coverImageSrc, coverWidth, coverFade, backgroundColor }: DesignPreviewCoverProps) => (
    <div
      aria-hidden="true"
      className={cn(
        'relative shrink-0 ads:bg-ads-gray-4 bg-center bg-cover bg-gray-4 bg-no-repeat',
        // `immersive` is taller so the cover visibly bleeds below the overlaid header bar.
        coverWidth === 'immersive' ? 'h-12' : 'h-9',
        // `inset` mirrors the page's contained cover: content-column width with the page bg in the
        // side gutters, rounded top corners flush against the content panel below.
        coverWidth === 'inset' ? 'mx-auto w-[93%] overflow-hidden rounded-t-4' : 'w-full',
      )}
      data-testid="SHARE_DESIGN_TAB_PREVIEW_COVER_IMAGE"
      style={{ backgroundImage: `url(${coverImageSrc})` }}
    >
      {coverFade ? (
        <div
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-1/2"
          data-testid="SHARE_DESIGN_TAB_PREVIEW_COVER_FADE"
          style={{ background: `linear-gradient(to bottom, transparent, ${backgroundColor})` }}
        />
      ) : null}
    </div>
  ),
);

DesignPreviewCover.displayName = 'DesignPreviewCover';
